import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ActionIcon,
  Box,
  Group,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconBuilding,
  IconGripVertical,
  IconTrash,
  IconUser,
} from "@tabler/icons-react";
import { FieldRenderer } from "../FieldRenderer";
import { FormTemplateField } from "../../../types/formTemplates";
import { FIELD_TYPE_META } from "../constants";
import { FieldConfigEditor } from "./fieldConfigEditors/FieldConfigEditor";

interface SortableFieldCardProps {
  field: FormTemplateField;
  index: number;
  total: number;
  onUpdate: (field: FormTemplateField) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  error?: string | null;
}

export const SortableFieldCard: React.FC<SortableFieldCardProps> = ({
  field,
  index,
  total,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  error,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `field:${field.id}`, data: { kind: "field" } });

  const meta = FIELD_TYPE_META[field.type];
  const isLayout = meta.isLayoutOnly;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={{
        ...style,
        border: error
          ? "1px solid var(--mantine-color-red-4)"
          : "1px solid var(--mantine-color-gray-2)",
        borderRadius: 8,
        background: "var(--mantine-color-white)",
        padding: 12,
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb="xs">
        <Group gap="xs" align="center" wrap="nowrap" style={{ flex: 1 }}>
          <Box
            {...attributes}
            {...listeners}
            style={{
              cursor: "grab",
              color: "var(--mantine-color-gray-5)",
              touchAction: "none",
            }}
            aria-label="Drag to reorder"
          >
            <IconGripVertical size={16} />
          </Box>
          {!isLayout && (
            <TextInput
              value={field.label}
              onChange={(e) =>
                onUpdate({ ...field, label: e.currentTarget.value })
              }
              placeholder="Field label"
              style={{ flex: 1 }}
              size="xs"
            />
          )}
          {isLayout && field.type === "section_header" && (
            <TextInput
              value={(field.config as { text: string }).text || ""}
              onChange={(e) =>
                onUpdate({
                  ...field,
                  label: e.currentTarget.value,
                  config: { ...field.config, text: e.currentTarget.value },
                })
              }
              placeholder="Section heading"
              style={{ flex: 1 }}
              size="xs"
            />
          )}
          {isLayout && field.type === "section_description" && (
            <Text size="xs" fw={500} c="dimmed" style={{ flex: 1 }}>
              {meta.label}
            </Text>
          )}
          {isLayout && field.type === "page_break" && (
            <Box
              style={{
                flex: 1,
                fontSize: 12,
                fontWeight: 600,
                color: "var(--mantine-color-gray-6)",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Page break
            </Box>
          )}
        </Group>

        <Group gap={4}>
          <Tooltip label="Move up">
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={index === 0}
              onClick={onMoveUp}
              aria-label="Move field up"
            >
              <IconArrowUp size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Move down">
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={index === total - 1}
              onClick={onMoveDown}
              aria-label="Move field down"
            >
              <IconArrowDown size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete field">
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={onRemove}
              aria-label="Delete field"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {isLayout && field.type === "section_description" && (
        <Box mb="xs">
          <FieldConfigEditor field={field} onUpdate={onUpdate} />
        </Box>
      )}

      {!isLayout && (
        <Group gap="xs" mb="xs" wrap="wrap">
          <SegmentedControl
            size="xs"
            value={field.actor ?? "staff"}
            onChange={(v) =>
              onUpdate({ ...field, actor: v as "staff" | "client" })
            }
            data={[
              {
                value: "staff",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconBuilding size={12} /> Staff
                  </Group>
                ),
              },
              {
                value: "client",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconUser size={12} /> Client
                  </Group>
                ),
              },
            ]}
          />
          <SegmentedControl
            size="xs"
            value={String(field.layout?.colSpan ?? 12)}
            onChange={(v) =>
              onUpdate({
                ...field,
                layout: { colSpan: Number(v) === 6 ? 6 : 12 },
              })
            }
            data={[
              { value: "12", label: "Full" },
              { value: "6", label: "Half" },
            ]}
          />
          <Switch
            checked={field.required}
            onChange={(e) =>
              onUpdate({ ...field, required: e.currentTarget.checked })
            }
            label="Required"
            size="xs"
          />
        </Group>
      )}

      {!isLayout && (
        <Stack gap="xs">
          <FieldRenderer field={field} mode="preview" />
          <FieldConfigEditor field={field} onUpdate={onUpdate} />
        </Stack>
      )}

      {isLayout && field.type === "divider" && (
        <Box
          my="xs"
          style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
        />
      )}

      {error && (
        <Box
          mt="xs"
          p="xs"
          style={{
            background: "var(--mantine-color-red-0)",
            border: "1px solid var(--mantine-color-red-2)",
            borderRadius: 4,
            color: "var(--mantine-color-red-8)",
            fontSize: 12,
          }}
        >
          {error}
        </Box>
      )}
    </Box>
  );
};
